import React, { useState, useEffect } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { useApp } from "../context/AppContext";
import api, { getImageUrl, getBackendOrigin } from "../api/client";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock,
  FileText, User, CheckCircle2, AlertCircle, Search, Sparkles, UserCheck, UserX,
  ListChecks, MapPin, Star, ThumbsUp, XCircle, CheckCircle, Image as ImageIcon
} from "lucide-react";

export function ManagerReportsCalendarScreen() {
  const { team, fetchAllDailyReports, fetchCalendarSummary, tasks: contextTasks, rateTask, approveTask, rejectTask } = useApp();

  const [activeTab, setActiveTab] = useState("daily"); // "daily" or "tasks"

  // Selected Date state
  const getTodayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [selectedDate, setSelectedDate] = useState(getTodayStr());

  // Current Month/Year for calendar view
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth()); // 0-indexed

  // Calendar summary data for daily reports
  const [calendarSummary, setCalendarSummary] = useState({});
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  // Daily reports for selected date
  const [selectedDateReports, setSelectedDateReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Task reports data
  const [allTasks, setAllTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Rating modal / inline rating state for task or report
  const [ratingTaskId, setRatingTaskId] = useState(null);
  const [ratingReportId, setRatingReportId] = useState(null);
  const [ratingVal, setRatingVal] = useState(5);
  const [feedbackText, setFeedbackText] = useState("");
  const [ratingLoading, setRatingLoading] = useState(false);

  // Search filter
  const [search, setSearch] = useState("");

  // Announcements & Holidays state
  const [holidays, setHolidays] = useState([]);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [announcementType, setAnnouncementType] = useState("combined_task");
  const [startDate, setStartDate] = useState(selectedDate);
  const [endDate, setEndDate] = useState(selectedDate);
  const [targetTeam, setTargetTeam] = useState("ALL");
  const [holidayTitle, setHolidayTitle] = useState("");
  const [holidayDesc, setHolidayDesc] = useState("");
  const [savingHoliday, setSavingHoliday] = useState(false);

  const loadHolidays = async () => {
    try {
      const res = await api.get("/reports/announcements");
      if (res.data && res.data.announcements) {
        setHolidays(res.data.announcements);
      }
    } catch (err) {
      console.error("Error fetching announcements:", err);
    }
  };

  const handleOpenHolidayModal = (dateStr) => {
    const targetDate = dateStr || selectedDate;
    setStartDate(targetDate);
    setEndDate(targetDate);
    setAnnouncementType("combined_task");
    setHolidayTitle("");
    setHolidayDesc("");
    setIsHolidayModalOpen(true);
  };

  const handleSaveHoliday = async (e) => {
    e.preventDefault();
    if (!startDate || !holidayTitle.trim()) return alert("Start Date and Title are required.");
    setSavingHoliday(true);
    try {
      await api.post("/reports/announcements", {
        title: holidayTitle,
        description: holidayDesc,
        type: announcementType,
        startDate: startDate,
        endDate: endDate || startDate,
        targetTeam: targetTeam || "ALL"
      });
      
      const titleSaved = holidayTitle;
      const descSaved = holidayDesc;
      const startSaved = startDate;
      const endSaved = endDate;

      setIsHolidayModalOpen(false);
      setHolidayTitle("");
      setHolidayDesc("");
      await loadHolidays();

      // Trigger WhatsApp send to all active employees
      setTimeout(() => {
        if (window.confirm("Notice saved successfully! Do you want to send this announcement to all employees on WhatsApp?")) {
          const msgText = `📢 *GoldPE WIBIL Notice*\n` +
                          `📅 Date: ${startSaved}${endSaved && endSaved !== startSaved ? ` to ${endSaved}` : ""}\n\n` +
                          `🔔 *${titleSaved}*\n` +
                          `${descSaved ? `📝 ${descSaved}\n\n` : ""}` +
                          `_Sent via GoldPE WIBIL Manager Portal_`;

          const activeEmployees = team.filter(e => (e.role || "").toLowerCase() !== "manager" && !e.is_disabled);
          activeEmployees.forEach((emp, index) => {
            setTimeout(() => {
              const encodedText = encodeURIComponent(msgText);
              const phone = emp.phone ? emp.phone.replace(/[^0-9]/g, "") : "";
              const targetPhone = phone.length === 10 ? `91${phone}` : phone;
              window.open(`https://api.whatsapp.com/send?phone=${targetPhone}&text=${encodedText}`, "_blank");
            }, index * 1200);
          });
        }
      }, 500);

    } catch (err) {
      alert(err.response?.data?.error || "Failed to save announcement/holiday");
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (targetId) => {
    if (!confirm(`Remove announcement/holiday?`)) return;
    try {
      await api.delete(`/reports/announcements/${targetId}`);
      await loadHolidays();
    } catch (err) {
      alert("Failed to delete announcement.");
    }
  };

  const getMonthStr = (yr, mo) => `${yr}-${String(mo + 1).padStart(2, "0")}`;

  // Fetch all tasks for manager
  const loadAllTasks = async (silent = false) => {
    try {
      if (!silent) setLoadingTasks(true);
      const res = await api.get("/tasks/all");
      setAllTasks(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setAllTasks(contextTasks || []);
    } finally {
      if (!silent) setLoadingTasks(false);
    }
  };

  // Load calendar summary counts for daily reports
  const loadCalendarData = async (silent = false) => {
    try {
      if (!silent) setLoadingCalendar(true);
      const monthStr = getMonthStr(currentYear, currentMonth);
      const data = await fetchCalendarSummary(monthStr);
      setCalendarSummary(data.summary || {});
    } catch (err) {
      console.error("Error loading calendar summary:", err);
    } finally {
      if (!silent) setLoadingCalendar(false);
    }
  };

  // Load daily reports for selected date
  const loadReportsForDate = async (dateStr, silent = false) => {
    try {
      if (!silent) setLoadingReports(true);
      const reports = await fetchAllDailyReports({ date: dateStr });
      setSelectedDateReports(reports || []);
    } catch (err) {
      console.error("Error loading date reports:", err);
    } finally {
      if (!silent) setLoadingReports(false);
    }
  };

  useEffect(() => {
    loadCalendarData(false);
    loadAllTasks(false);
    loadHolidays();
  }, [currentYear, currentMonth]);

  useEffect(() => {
    if (selectedDate) {
      loadReportsForDate(selectedDate, false);
    }
  }, [selectedDate]);

  // Calendar Month Navigation
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Generate days in grid for selected month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
  const firstDayOfWeek = firstDayIndex;

  const daysGrid = [];
  for (let i = 0; i < firstDayIndex; i++) {
    daysGrid.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dayStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    daysGrid.push(dayStr);
  }

  // Helper to extract date YYYY-MM-DD from task completion/created timestamp
  const getTaskDate = (task) => {
    if (task.completed_at) return String(task.completed_at).slice(0, 10);
    if (task.created_at) return String(task.created_at).slice(0, 10);
    return getTodayStr();
  };

  // Build Task Calendar map: date -> array of tasks
  const tasksByDateMap = {};
  allTasks.forEach((t) => {
    const d = getTaskDate(t);
    if (!tasksByDateMap[d]) tasksByDateMap[d] = [];
    tasksByDateMap[d].push(t);
  });

  // Filter tasks for selected date
  const tasksForSelectedDate = (tasksByDateMap[selectedDate] || []).filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (t.title && t.title.toLowerCase().includes(q)) ||
      (t.employee_name && t.employee_name.toLowerCase().includes(q)) ||
      (t.employee_id && String(t.employee_id).toLowerCase().includes(q)) ||
      (t.location && t.location.toLowerCase().includes(q)) ||
      (t.category && t.category.toLowerCase().includes(q))
    );
  });

  // Handle task approval / rejection / rating
  const handleRateSubmit = async (taskId) => {
    setRatingLoading(true);
    try {
      if (rateTask) {
        await rateTask(taskId, ratingVal, feedbackText);
      } else {
        await api.patch(`/tasks/${taskId}/rate`, { rating: ratingVal, feedback: feedbackText });
      }
      setRatingTaskId(null);
      setFeedbackText("");
      await loadAllTasks(true);
      alert("Task rating and feedback submitted successfully!");
    } catch (e) {
      console.error("Task rate error:", e);
      alert(e.response?.data?.error || "Failed to rate task");
    } finally {
      setRatingLoading(false);
    }
  };

  const handleReportRateSubmit = async (reportId) => {
    setRatingLoading(true);
    try {
      await api.patch(`/reports/${reportId}/rate`, {
        rating: ratingVal,
        feedback: feedbackText
      });
      setRatingReportId(null);
      setFeedbackText("");
      await loadReportsForDate(selectedDate, true);
      alert("Daily report rating and feedback submitted successfully!");
    } catch (e) {
      console.error("Report rate error:", e);
      alert(e.response?.data?.error || "Failed to rate report");
    } finally {
      setRatingLoading(false);
    }
  };

  const handleApprove = async (taskId) => {
    try {
      if (approveTask) {
        await approveTask(taskId);
      } else {
        await api.patch(`/tasks/${taskId}/approve`);
      }
      await loadAllTasks(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleReject = async (taskId) => {
    const reason = prompt("Enter reason for rejecting task completion:");
    if (reason === null) return;
    try {
      if (rejectTask) {
        await rejectTask(taskId, reason);
      } else {
        await api.patch(`/tasks/${taskId}/reject`, { reason });
      }
      await loadAllTasks(true);
    } catch (e) {
      console.error(e);
    }
  };

  // Map employees to reports for selected date
  const matchedReportIds = new Set();

  const teamReportsMap = team.map((emp) => {
    const report = selectedDateReports.find((r) => {
      const rEmpId = String(r.employeeId || r.employee_id || "").toUpperCase();
      const empIdStr = String(emp.employee_id || emp.employeeId || "").toUpperCase();
      const empIdNum = String(emp.id || "");
      const isMatch = rEmpId && (rEmpId === empIdStr || rEmpId === empIdNum);
      if (isMatch) matchedReportIds.add(r.id);
      return isMatch;
    });
    return { emp, report };
  });

  // Include reports submitted by employees not currently in team list
  const extraReports = selectedDateReports
    .filter((r) => !matchedReportIds.has(r.id))
    .map((r) => ({
      emp: {
        id: r.employeeId || r.id,
        employee_id: r.employeeId || "EMP",
        name: r.employeeName || r.employeeId || "Employee",
        role: r.role || "Team Member",
        initials: r.initials || "EM",
      },
      report: r
    }));

  const allReportsMap = [...teamReportsMap, ...extraReports];

  const filteredTeamReportsMap = allReportsMap.filter(({ emp, report }) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (emp.name && emp.name.toLowerCase().includes(q)) ||
      (emp.employee_id && String(emp.employee_id).toLowerCase().includes(q)) ||
      (report && report.work && report.work.toLowerCase().includes(q))
    );
  });

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* Top Banner */}
      <Card style={{
        background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        color: "#fff", marginBottom: 20, position: "relative", overflow: "hidden", padding: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 46, height: 46, borderRadius: 14,
              background: `${TOKENS.gold}22`, border: `1.5px solid ${TOKENS.gold}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {activeTab === "daily" ? <CalendarIcon size={24} color={TOKENS.gold} /> : <ListChecks size={24} color={TOKENS.gold} />}
            </div>
            <div>
              <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "#fff", margin: 0 }}>
                Everyday & Task Reports Calendar
              </h2>
              <p style={{ fontSize: 12.5, color: TOKENS.goldLight, margin: "4px 0 0", fontWeight: 500 }}>
                Select any date on the calendar to view both daily work submissions and task completions
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => handleOpenHolidayModal(selectedDate)}
              style={{
                background: "#f59e0b", border: "none",
                borderRadius: 10, padding: "8px 14px", color: "#fff", fontSize: 12, fontWeight: 700,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                boxShadow: "0 2px 8px rgba(245,158,11,0.25)"
              }}
            >
              📅 Manage Calendar ({selectedDate})
            </button>
            <button
              onClick={() => {
                const token = localStorage.getItem("fp_token");
                window.open(`${getBackendOrigin()}/api/export/daily-reports?token=${encodeURIComponent(token || "")}`, "_blank");
                window.open(`${getBackendOrigin()}/api/export/tasks?token=${encodeURIComponent(token || "")}`, "_blank");
              }}
              style={{
                background: TOKENS.gold, border: "none",
                borderRadius: 10, padding: "8px 14px", color: TOKENS.navyDeep, fontSize: 12, fontWeight: 700,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              }}
            >
              📊 Export Reports to Excel
            </button>
          </div>
        </div>
      </Card>

      {/* Selected Date Holiday Banner */}
      {(() => {
        const selHoliday = holidays.find(h => h.date === selectedDate);
        if (!selHoliday) return null;
        return (
          <Card style={{
            background: "linear-gradient(135deg, #fffbe6, #fef3c7)",
            border: "1.5px solid #f59e0b", padding: "14px 18px", marginBottom: 20,
            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 24 }}>🎉</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#b45309" }}>
                  Holiday: {selHoliday.title}
                </div>
                {selHoliday.description && (
                  <div style={{ fontSize: 12, color: "#92400e", marginTop: 2 }}>
                    {selHoliday.description}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => handleOpenHolidayModal(selectedDate)}
                style={{ background: "#f59e0b", color: "#fff", border: "none", padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteHoliday(selectedDate)}
                style={{ background: "#ef4444", color: "#fff", border: "none", padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
              >
                Remove
              </button>
            </div>
          </Card>
        );
      })()}

      {/* Tabs removed, both views will be shown below */}

      {/* Calendar Section */}
      <Card style={{ padding: "14px 12px", marginBottom: 24, overflowX: "auto" }}>
        {/* Month Selector Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep }}>
              {monthNames[currentMonth]} {currentYear}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button onClick={prevMonth} style={{
              background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
              borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center"
            }}>
              <ChevronLeft size={18} color={TOKENS.navyDeep} />
            </button>
            <button onClick={() => {
              const now = new Date();
              setCurrentYear(now.getFullYear());
              setCurrentMonth(now.getMonth());
              setSelectedDate(getTodayStr());
            }} style={{
              background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
              borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, cursor: "pointer"
            }}>
              Today
            </button>
            <button onClick={() => handleOpenHolidayModal(selectedDate)} style={{
              background: "#fef3c7", border: "1px solid #f59e0b",
              borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: "#b45309", cursor: "pointer"
            }}>
              📅 Manage Calendar
            </button>
            <button onClick={nextMonth} style={{
              background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
              borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center"
            }}>
              <ChevronRight size={18} color={TOKENS.navyDeep} />
            </button>
          </div>
        </div>

        {/* Mobile responsive Calendar Grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 4,
          width: "100%",
          textTransform: "uppercase"
        }}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
            <div key={day} style={{
              textAlign: "center", fontSize: 10, fontWeight: 700, color: TOKENS.muted, padding: "4px 0"
            }}>
              {day}
            </div>
          ))}

          {/* Empty Days before 1st of month */}
          {Array.from({ length: firstDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} style={{ minHeight: 44, background: "transparent" }} />
          ))}

          {/* Days of month */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const dayNum = i + 1;
            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
            const isSelected = dateStr === selectedDate;
            const isToday = dateStr === getTodayStr();

            // Holiday check
            const holidayObj = holidays.find(h => h.date === dateStr);
            const isHoliday = !!holidayObj;

            // Daily Report Stats for day
            const dayReportData = calendarSummary[dateStr];
            const reportCount = dayReportData ? dayReportData.count : 0;

            // Task Stats for day
            const dayTasks = tasksByDateMap[dateStr] || [];
            const taskCount = dayTasks.length;
            const completedTaskCount = dayTasks.filter(t => t.status === "done" || t.status === "completed").length;

            return (
              <div
                key={dateStr}
                onClick={() => setSelectedDate(dateStr)}
                style={{
                  minHeight: 46, borderRadius: 8, padding: "4px 4px", cursor: "pointer",
                  display: "flex", flexDirection: "column", justifyContent: "space-between",
                  border: isHoliday ? `1.5px solid #f59e0b` : isSelected ? `2px solid ${TOKENS.gold}` : isToday ? `1.5px solid ${TOKENS.navyDeep}` : `1px solid ${TOKENS.border}`,
                  background: isHoliday ? "#fef3c7" : isSelected ? `${TOKENS.gold}18` : isToday ? "rgba(10,25,50,0.03)" : "#fff",
                  transition: "all 0.15s ease",
                  overflow: "hidden"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{
                    fontSize: 11.5, fontWeight: isSelected || isToday || isHoliday ? 800 : 600,
                    color: isHoliday ? "#b45309" : isSelected ? TOKENS.navyDeep : isToday ? TOKENS.gold : TOKENS.ink,
                  }}>
                    {dayNum}
                  </span>
                  {isToday && (
                    <span style={{ fontSize: 7, background: TOKENS.navyDeep, color: TOKENS.gold, padding: "1px 3px", borderRadius: 3, fontWeight: 700 }}>
                      TODAY
                    </span>
                  )}
                </div>

                {/* Holiday Label or Badges */}
                {isHoliday ? (
                  <div style={{
                    fontSize: 8.5, fontWeight: 800, color: "#b45309",
                    background: "#fde68a", borderRadius: 4, padding: "1px 2px", textAlign: "center",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                  }} title={holidayObj.title}>
                    🎉 {holidayObj.title}
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 2, flexDirection: "column" }}>
                    {reportCount > 0 && (
                      <div style={{
                        fontSize: 9, fontWeight: 700, color: TOKENS.success,
                        background: `${TOKENS.success}18`, borderRadius: 4, padding: "1px 2px", textAlign: "center",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                      }}>
                        {reportCount} reps
                      </div>
                    )}
                    {taskCount > 0 && (
                      <div style={{
                        fontSize: 9, fontWeight: 700, color: completedTaskCount > 0 ? TOKENS.gold : TOKENS.blue,
                        background: completedTaskCount > 0 ? `${TOKENS.gold}22` : `${TOKENS.blue}15`,
                        borderRadius: 4, padding: "1px 2px", textAlign: "center",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                      }}>
                        {completedTaskCount}/{taskCount} tsks
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Search & Filter Header for Selected Date */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <SectionLabel style={{ margin: 0 }}>
          📄 Everyday & 📋 Task Reports for {selectedDate}
        </SectionLabel>

        {/* Search Input */}
        <div style={{ position: "relative", width: 220 }}>
          <Search size={14} color={TOKENS.muted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
          <input
            type="text"
            placeholder="Search employee or task…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "6px 10px 6px 30px", borderRadius: 8,
              border: `1px solid ${TOKENS.border}`, fontSize: 12, outline: "none"
            }}
          />
        </div>
      </div>

      {/* Tab Selector */}
      <div style={{ display: "flex", gap: 8, background: "#f1f5f9", padding: 4, borderRadius: 10, marginBottom: 16 }}>
        <button
          onClick={() => setActiveTab("daily")}
          style={{
            flex: 1, padding: "8px 12px", borderRadius: 8, border: "none",
            background: activeTab === "daily" ? "#fff" : "transparent",
            color: activeTab === "daily" ? TOKENS.navyDeep : TOKENS.muted,
            fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all 0.2s ease",
            boxShadow: activeTab === "daily" ? "0 2px 8px rgba(0,0,0,0.05)" : "none",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6
          }}
        >
          <FileText size={16} /> Everyday Reports ({filteredTeamReportsMap.length})
        </button>
        <button
          onClick={() => setActiveTab("tasks")}
          style={{
            flex: 1, padding: "8px 12px", borderRadius: 8, border: "none",
            background: activeTab === "tasks" ? "#fff" : "transparent",
            color: activeTab === "tasks" ? TOKENS.navyDeep : TOKENS.muted,
            fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all 0.2s ease",
            boxShadow: activeTab === "tasks" ? "0 2px 8px rgba(0,0,0,0.05)" : "none",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6
          }}
        >
          <ListChecks size={16} /> Task Reports ({tasksForSelectedDate.length})
        </button>
      </div>

      {/* CONTENT LIST FOR DAILY REPORTS AND TASKS */}
      <div style={{ display: "flex", gap: 20, flexDirection: "column" }}>
        
        {/* Daily Reports Section */}
        {activeTab === "daily" && (
          <div>
            <SectionLabel>📄 Everyday Reports</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {loadingReports ? (
            <Card style={{ padding: 24, textAlign: "center", color: TOKENS.muted }}>
              Loading reports for {selectedDate}…
            </Card>
          ) : filteredTeamReportsMap.length === 0 ? (
            <Card style={{ padding: 24, textAlign: "center", color: TOKENS.muted }}>
              No employee reports found for {selectedDate}.
            </Card>
          ) : (
            filteredTeamReportsMap.map(({ emp, report }, idx) => {
              const rating = report ? (report.manager_rating || report.managerRating) : null;
              const feedback = report ? (report.manager_feedback || report.managerFeedback) : null;
              const photo = report ? (report.reportPhoto || (report.report_photo ? getImageUrl(report.report_photo) : null)) : null;

              return (
                <Card key={emp?.id || emp?.employee_id || idx} style={{ padding: 16 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: "50%", background: TOKENS.navyDeep,
                        color: TOKENS.gold, display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 14, fontWeight: 700, flexShrink: 0
                      }}>
                        {emp?.initials || "EM"}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.ink }}>{emp?.name || "Employee"}</div>
                        <div style={{ fontSize: 11.5, color: TOKENS.muted }}>{emp?.role || "Team Member"} · ID: {emp?.employee_id || emp?.id}</div>
                      </div>
                    </div>

                    {report ? (
                      <StatusPill status="Submitted" color={TOKENS.success} />
                    ) : (
                      <StatusPill status="Not Submitted" color={TOKENS.danger} />
                    )}
                  </div>

                  {report ? (
                    <>
                      <div style={{ marginTop: 14, background: TOKENS.cream, padding: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 4 }}>Work Summary:</div>
                        <p style={{ fontSize: 13, color: TOKENS.ink, margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                          {report.work}
                        </p>
                        {(report.timeSpent || report.time_spent) && (
                          <div style={{ fontSize: 11.5, color: TOKENS.navyDeep, fontWeight: 600, marginTop: 4 }}>
                            ⏱️ Time Logged: {report.timeSpent || report.time_spent}
                          </div>
                        )}
                        {photo && (
                          <div style={{ marginTop: 8 }}>
                            <a
                              href={photo}
                              target="_blank"
                              rel="noreferrer"
                              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: TOKENS.blue, textDecoration: "none" }}
                            >
                              <ImageIcon size={14} /> View Progress Photo Proof
                            </a>
                          </div>
                        )}
                        {(report.submitted_at || report.submittedAt) && (
                          <div style={{ fontSize: 10.5, color: TOKENS.muted, marginTop: 8 }}>
                            Submitted at: {new Date(report.submitted_at || report.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>

                      {/* Manager Rating Display / Controls */}
                      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${TOKENS.border}` }}>
                        {rating ? (
                          <div style={{ background: `${TOKENS.gold}12`, borderRadius: 10, padding: 10, border: `1px solid ${TOKENS.gold}30` }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <span>Manager Rating:</span>
                              <span style={{ color: TOKENS.gold, fontSize: 14 }}>{"★".repeat(rating)} ({rating}/5)</span>
                            </div>
                            {feedback && (
                              <div style={{ fontSize: 12, color: TOKENS.ink, fontStyle: "italic", marginTop: 4 }}>
                                "{feedback}"
                              </div>
                            )}
                            <button
                              onClick={() => {
                                setRatingReportId(report.id);
                                setRatingVal(rating || 5);
                                setFeedbackText(feedback || "");
                              }}
                              style={{ marginTop: 6, background: "transparent", border: "none", color: TOKENS.navyDeep, fontSize: 11, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
                            >
                              Edit Rating / Review
                            </button>
                          </div>
                        ) : ratingReportId === report.id ? (
                          <div style={{ background: "#fff", border: `1.5px solid ${TOKENS.navyDeep}40`, borderRadius: 12, padding: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 6 }}>
                              ⭐ Rate Daily Work & Give Feedback:
                            </div>
                            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setRatingVal(star)}
                                  style={{
                                    background: ratingVal >= star ? TOKENS.gold : TOKENS.cream,
                                    color: ratingVal >= star ? "#fff" : TOKENS.ink,
                                    border: `1px solid ${TOKENS.border}`,
                                    borderRadius: 8, padding: "4px 8px", fontWeight: 700, fontSize: 14, cursor: "pointer"
                                  }}
                                >
                                  ★ {star}
                                </button>
                              ))}
                            </div>
                            <input
                              type="text"
                              placeholder="Enter feedback/comments for employee..."
                              value={feedbackText}
                              onChange={(e) => setFeedbackText(e.target.value)}
                              style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 12, marginBottom: 8 }}
                            />
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                onClick={() => setRatingReportId(null)}
                                style={{ flex: 1, padding: "6px 0", borderRadius: 8, border: `1px solid ${TOKENS.border}`, background: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleReportRateSubmit(report.id)}
                                disabled={ratingLoading}
                                style={{ flex: 1, padding: "6px 0", borderRadius: 8, border: "none", background: TOKENS.navyDeep, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                              >
                                Submit Rating
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setRatingReportId(report.id);
                              setRatingVal(5);
                              setFeedbackText("");
                            }}
                            style={{
                              background: TOKENS.goldBg, border: `1px solid ${TOKENS.gold}40`,
                              color: TOKENS.navyDeep, borderRadius: 8, padding: "6px 12px",
                              fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4
                            }}
                          >
                            <Star size={14} color={TOKENS.gold} /> Rate & Review Work Report
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div style={{ marginTop: 10, fontSize: 12, color: TOKENS.muted, fontStyle: "italic" }}>
                      No daily report submitted for this date.
                    </div>
                  )}
                </Card>
              );
            })
          )}
          </div>
          </div>
        )}

        {/* Task Reports Section */}
        {activeTab === "tasks" && (
          <div>
            <SectionLabel>📋 Task Reports</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {loadingTasks ? (
            <Card style={{ padding: 24, textAlign: "center", color: TOKENS.muted }}>
              Loading task reports for {selectedDate}…
            </Card>
          ) : tasksForSelectedDate.length === 0 ? (
            <Card style={{ padding: 24, textAlign: "center", color: TOKENS.muted }}>
              No tasks recorded or completed on {selectedDate}.
            </Card>
          ) : (
            tasksForSelectedDate.map((t) => {
              const isDone = t.status === "done" || t.status === "completed";
              const isApproved = t.approval_status === "approved";
              const isPendingApproval = isDone && t.approval_status === "pending";

              return (
                <Card key={t.id} style={{ padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, background: `${TOKENS.navyMid}15`, color: TOKENS.navyDeep, padding: "2px 8px", borderRadius: 6 }}>
                          {t.category || "General"}
                        </span>
                        <span style={{ fontSize: 11, color: TOKENS.muted }}>ID: {t.id}</span>
                      </div>
                      <h4 style={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink, margin: "2px 0 4px" }}>
                        {t.title}
                      </h4>
                      <div style={{ fontSize: 12, color: TOKENS.muted, display: "flex", alignItems: "center", gap: 12 }}>
                        <span>👤 <strong>{t.employee_name || t.employee_id}</strong></span>
                        <span>📍 {t.location || "On-site"}</span>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                      {isApproved ? (
                        <StatusPill status="Approved ✅" color={TOKENS.success} />
                      ) : isPendingApproval ? (
                        <StatusPill status="Awaiting Approval ⏳" color={TOKENS.warning} />
                      ) : isDone ? (
                        <StatusPill status="Completed" color={TOKENS.gold} />
                      ) : (
                        <StatusPill status="Pending" color={TOKENS.muted} />
                      )}
                      {t.manager_rating && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                          <div>{"★".repeat(t.manager_rating)}</div>
                          {t.manager_feedback && (
                            <div style={{ fontSize: 11, color: TOKENS.ink, fontStyle: "italic", marginTop: 2, fontWeight: 500, maxWidth: 200, textAlign: "right" }}>
                              "{t.manager_feedback}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Task details & completion remarks */}
                  {(t.completion_remarks || t.task_photo) && (
                    <div style={{ marginTop: 12, background: TOKENS.cream, padding: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }}>
                      {t.completion_remarks && (
                        <div style={{ fontSize: 12.5, color: TOKENS.ink, marginBottom: 6 }}>
                          <strong>Completion Remarks:</strong> {t.completion_remarks}
                        </div>
                      )}
                      {t.task_photo && (
                        <div style={{ marginTop: 8 }}>
                          <a href={getImageUrl(t.task_photo)} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: TOKENS.blue, textDecoration: "none" }}>
                            <ImageIcon size={14} /> View Attached Photo
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Manager Rating & Action buttons */}
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${TOKENS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontSize: 11.5, color: TOKENS.muted }}>
                      {t.time && <span>Time: {t.time} · </span>}
                      {t.client_name && <span>Client: {t.client_name}</span>}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {isPendingApproval && (
                        <>
                          <button onClick={() => handleApprove(t.id)} style={{
                            background: TOKENS.success, color: "#fff", border: "none", borderRadius: 8,
                            padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                          }}>
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button onClick={() => handleReject(t.id)} style={{
                            background: TOKENS.danger, color: "#fff", border: "none", borderRadius: 8,
                            padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                          }}>
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      )}

                      {isDone && (
                        <button onClick={() => {
                          setRatingTaskId(ratingTaskId === t.id ? null : t.id);
                          setRatingVal(t.manager_rating || 5);
                          setFeedbackText(t.manager_feedback || "");
                        }} style={{
                          background: `${TOKENS.gold}22`, color: TOKENS.navyDeep, border: `1px solid ${TOKENS.gold}`, borderRadius: 8,
                          padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                        }}>
                          <Star size={14} color={TOKENS.gold} fill={TOKENS.gold} /> {t.manager_rating ? "Update Rating" : "Rate Task"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Inline Task Rating Form */}
                  {ratingTaskId === t.id && (
                    <div style={{ marginTop: 12, padding: 12, background: "rgba(245,184,0,0.08)", borderRadius: 10, border: `1px solid ${TOKENS.gold}40` }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 8 }}>Rate Task Performance (1 - 5 Stars):</div>
                      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setRatingVal(star)}
                            style={{
                              background: star <= ratingVal ? TOKENS.gold : "#E2E8F0",
                              border: "none", borderRadius: 6, width: 32, height: 32, cursor: "pointer",
                              color: star <= ratingVal ? "#000" : "#64748B", fontWeight: 800, fontSize: 14
                            }}
                          >
                            ★
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        placeholder="Add manager feedback/comments (optional)…"
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 6, border: `1px solid ${TOKENS.border}`, fontSize: 12, marginBottom: 10, outline: "none" }}
                      />
                      <button
                        onClick={() => handleRateSubmit(t.id)}
                        disabled={ratingLoading}
                        style={{
                          background: TOKENS.navyDeep, color: TOKENS.gold, border: "none", borderRadius: 8,
                          padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer"
                        }}
                      >
                        Submit Rating
                      </button>
                    </div>
                  )}
                </Card>
              );
            })
          )}
          </div>
          </div>
        )}

      {/* Holiday Edit Modal */}
      {isHolidayModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 16
        }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440, padding: 24, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                📢 Publish Notice / Holiday / Task
              </h3>
              <button onClick={() => setIsHolidayModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: TOKENS.muted }}>✕</button>
            </div>

            <form onSubmit={handleSaveHoliday}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Notice Type *</label>
                <select
                  value={announcementType}
                  onChange={(e) => setAnnouncementType(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none", background: "#fff" }}
                >
                  <option value="combined_task">📋 Combined Team Task</option>
                  <option value="holiday">🎉 Company Holiday</option>
                  <option value="announcement">📢 Team Announcement</option>
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Title / Topic *</label>
                <input
                  type="text"
                  placeholder="e.g. Q3 Sales Target Sprint, Diwali Holiday"
                  value={holidayTitle}
                  onChange={(e) => setHolidayTitle(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                  required
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Start Date *</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Close / End Date *</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Target Audience</label>
                <select
                  value={targetTeam}
                  onChange={(e) => setTargetTeam(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none", background: "#fff" }}
                >
                  <option value="ALL">All Employees & Teams</option>
                  <option value="Field Operations">Field Operations</option>
                  <option value="Sales">Sales & Marketing</option>
                  <option value="Support">Support & Admin</option>
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Description / Guidelines (Optional)</label>
                <textarea
                  placeholder="Provide task goals, holiday instructions, or announcement guidelines…"
                  value={holidayDesc}
                  onChange={(e) => setHolidayDesc(e.target.value)}
                  rows={3}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                />
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setIsHolidayModalOpen(false)}
                  style={{ background: TOKENS.cream, color: TOKENS.ink, border: `1px solid ${TOKENS.border}`, borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingHoliday}
                  style={{ background: TOKENS.navyDeep, color: TOKENS.gold, border: "none", borderRadius: 8, padding: "10px 18px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                >
                  {savingHoliday ? "Publishing…" : "Publish & Notify Team"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
